import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeUp = ({ children, delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
};

const STEPS = [
  {
    number: '01',
    title: 'Create Your Account',
    body: 'Register with your email address and verify your identity via a one-time code sent to your inbox. Mino is open to users aged 18 and above. Your account is secured with encrypted authentication.',
  },
  {
    number: '02',
    title: 'Fund Your Wallet',
    body: 'Deposit funds via M-Pesa directly from your phone. Deposits are credited instantly to your Mino wallet. The minimum deposit is KES 30. All transactions are processed through a secure M-Pesa payment gateway.',
  },
  {
    number: '03',
    title: 'Browse Open Markets',
    body: 'Explore active prediction markets across politics, sports, finance, and current affairs. Each market displays real-time probability data, total trading volume, and a resolution date.',
  },
  {
    number: '04',
    title: 'Place Your Position',
    body: 'Select an outcome and enter your stake. The platform calculates your estimated return before you confirm. A 2% entry fee is applied at the time of trade. You may place multiple positions across different markets.',
  },
  {
    number: '05',
    title: 'Market Resolution',
    body: 'When the resolution date arrives, outcomes are verified against credible, publicly available sources — including established news outlets, official government records, and recognised data providers. No single source is used in isolation.',
  },
  {
    number: '06',
    title: 'Collect Your Returns',
    body: 'Winning positions are paid out from the total market pool, proportional to your stake relative to all winning positions. A 2% resolution fee and 20% withholding tax on net gains are deducted before payout. Funds are available for withdrawal immediately.',
  },
];

const FEES = [
  { label: 'Entry Fee',              value: '2%',      note: 'Deducted from your stake at the time of trade' },
  { label: 'Resolution Fee',         value: '2%',      note: 'Deducted from gross winnings at market resolution' },
  { label: 'Early Exit — Platform',  value: '1%',      note: 'Platform fee applied when exiting a position early' },
  { label: 'Early Exit — Penalty',   value: '20–30%',  note: '20% if >30 days remain on market, 30% if <30 days' },
  { label: 'Withholding Tax',        value: '20%',     note: 'Applied to net gains only, remitted to KRA on your behalf' },
  { label: 'Withdrawal Fee',         value: '2%',      note: 'Applies to withdrawals above KES 110 — free below that' },
];

const FAQS = [
  {
    q: 'What determines the probability shown on each market?',
    a: 'Probabilities are calculated in real time based on the distribution of stakes across all available outcomes. As more users trade, the displayed probabilities shift to reflect the collective position of all participants.',
  },
  {
    q: 'Can I exit a position before the market resolves?',
    a: 'Yes — early exit is available, subject to a 1% platform fee plus a tiered exit penalty (20% if more than 30 days remain on the market, 30% if fewer than 30 days remain). This model will evolve significantly as Mino grows.',
  },
  {
    q: 'How does Mino verify outcomes?',
    a: 'Outcomes are verified using credible, publicly available sources including established news organisations, official government publications, and recognised data providers. No single source is relied upon exclusively.',
  },
  {
    q: 'What happens if a market is voided?',
    a: 'In the event that a market cannot be fairly resolved — due to ambiguous outcomes, insufficient information, or other qualifying circumstances — all stakes are returned to participants, except the fees since its not refundable.',
  },
  {
    q: 'Is there a minimum or maximum stake?',
    a: 'The minimum stake per trade is KES 10. There is currently no stated maximum, though large positions are subject to internal risk review.',
  },
];

export default function HowItWorks() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [heroRef, heroVisible] = useInView(0.05);

  return (
    <div style={{ minHeight: '100vh', background: '#000000', fontFamily: "'Outfit', sans-serif", color: '#e8e6e0', position: 'relative', overflowX: 'hidden' }}>
      <Helmet>
        <title>How It Works — Mino Markets</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <meta name="description" content="Understand how Mino Markets prediction markets work — from account creation to payouts and market resolution." />
      </Helmet>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: rgba(255,255,255,0.12); }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

        .mino-aurora {
          position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden;
        }
        .mino-aurora::before {
          content: '';
          position: absolute;
          width: 140%; height: 55%;
          top: -18%; left: -20%;
          background: conic-gradient(from 200deg at 50% 50%,
            transparent 0deg,
            rgba(55,114,255,0.09) 40deg,
            rgba(22,199,132,0.07) 80deg,
            rgba(124,58,237,0.08) 120deg,
            transparent 160deg
          );
          filter: blur(60px);
          animation: auroraShift 20s ease-in-out infinite alternate;
          transform-origin: center top;
        }
        .mino-aurora::after {
          content: '';
          position: absolute;
          width: 80%; height: 40%;
          bottom: 5%; right: -10%;
          background: conic-gradient(from 30deg at 50% 50%,
            transparent 0deg,
            rgba(22,199,132,0.06) 50deg,
            rgba(55,114,255,0.05) 100deg,
            transparent 150deg
          );
          filter: blur(70px);
          animation: auroraShift2 26s ease-in-out infinite alternate;
          transform-origin: center bottom;
        }
        .mino-dot-grid {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 85% 85% at 50% 40%, black 40%, transparent 100%);
        }
        .mino-beam {
          position: fixed; pointer-events: none; z-index: 0;
          width: 1px; top: 0; bottom: 0;
          background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.06) 60%, transparent 100%);
          animation: beamDrift var(--dur, 28s) ease-in-out infinite alternate;
        }
        @keyframes auroraShift {
          0%   { transform: rotate(-4deg) scaleX(1);    opacity: 1; }
          50%  { transform: rotate(2deg)  scaleX(1.08); opacity: 0.85; }
          100% { transform: rotate(-6deg) scaleX(0.96); opacity: 1; }
        }
        @keyframes auroraShift2 {
          0%   { transform: rotate(6deg)  scaleX(1);    opacity: 0.8; }
          50%  { transform: rotate(-3deg) scaleX(1.1);  opacity: 1; }
          100% { transform: rotate(8deg)  scaleX(0.92); opacity: 0.75; }
        }
        @keyframes beamDrift {
          0%   { left: var(--from); opacity: 0.5; }
          100% { left: var(--to);   opacity: 1; }
        }

        .mino-nav-link { font-family: 'Outfit', sans-serif; font-size: 13px; color: rgba(255,255,255,0.32); cursor: pointer; padding: 5px 12px; border-radius: 6px; transition: all 0.2s; font-weight: 400; }
        .mino-nav-link:hover { color: rgba(255,255,255,0.72); background: rgba(255,255,255,0.06); }

        .step-card { transition: border-color 0.3s ease, background 0.3s ease; }
        .step-card:hover { border-color: rgba(255,255,255,0.16) !important; background: rgba(255,255,255,0.04) !important; }

        .fee-row { transition: background 0.2s ease; }
        .fee-row:hover { background: rgba(255,255,255,0.04) !important; }

        .faq-item { transition: background 0.25s ease, border-color 0.25s ease; }
        .faq-item:hover { background: rgba(255,255,255,0.03) !important; }

        .mino-val-card { transition: border-color 0.3s ease, background 0.3s ease; }
        .mino-val-card:hover { border-color: rgba(255,255,255,0.16) !important; background: rgba(255,255,255,0.04) !important; }
      `}</style>

      <div className="mino-aurora" />
      <div className="mino-dot-grid" />
      <div className="mino-beam" style={{ '--from': '8%',  '--to': '12%', '--dur': '30s' }} />
      <div className="mino-beam" style={{ '--from': '38%', '--to': '42%', '--dur': '38s', opacity: 0.6 }} />
      <div className="mino-beam" style={{ '--from': '68%', '--to': '72%', '--dur': '25s', opacity: 0.4 }} />

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="26" height="26" viewBox="0 0 38 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#050505" stroke="#1a1a1e" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: '-0.3px', color: '#e8e6e0' }}>Mino</span>
        </div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {[['Markets', '/markets'], ['About', '/about'], ['Blog', '/blog']].map(([l, p]) => (
            <span key={p} className="mino-nav-link" onClick={() => navigate(p)}>{l}</span>
          ))}
        </div>
        <button onClick={() => navigate(-1)} style={{ background: 'transparent', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 20px', fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 400, cursor: 'pointer', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#e8e6e0'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
          Back
        </button>
      </nav>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>

        {/* Hero */}
        <div ref={heroRef} style={{ paddingTop: 120, paddingBottom: 100, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1)' }}>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>Platform Guide</p>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(48px,7vw,88px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-3px', color: '#e8e6e0', marginBottom: 36, maxWidth: 720 }}>
            How Mino<br />Works.
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, color: 'rgba(255,255,255,0.36)', lineHeight: 1.8, maxWidth: 480, fontWeight: 300 }}>
            A prediction markets platform where you trade on the outcomes of real-world events using real money. Every mechanic explained, nothing omitted.
          </p>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Steps */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>Step by Step</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {STEPS.map((step, i) => (
                <div key={step.number} className="step-card" style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 32, padding: '32px 0', borderBottom: i < STEPS.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none', alignItems: 'start', borderRadius: 4 }}>
                  <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.18)', letterSpacing: '0.08em', paddingTop: 3 }}>{step.number}</span>
                  <div>
                    <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, color: '#e8e6e0', marginBottom: 10, letterSpacing: '-0.2px' }}>{step.title}</p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.32)', lineHeight: 1.8, fontWeight: 300 }}>{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Fee Structure */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>Fee Structure</p>
            <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflow: 'hidden', background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 80px 1fr', padding: '12px 28px', borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)' }}>
                {['Fee Type', 'Rate', 'Applied When'].map(h => (
                  <span key={h} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.18)' }}>{h}</span>
                ))}
              </div>
              {FEES.map((fee, i) => (
                <div key={fee.label} className="fee-row" style={{ display: 'grid', gridTemplateColumns: '1.4fr 80px 1fr', padding: '20px 28px', borderBottom: i < FEES.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', alignItems: 'center' }}>
                  <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>{fee.label}</span>
                  <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 14, fontWeight: 600, color: '#e8e6e0' }}>{fee.value}</span>
                  <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', lineHeight: 1.55, fontWeight: 300 }}>{fee.note}</span>
                </div>
              ))}
            </div>
            <p style={{ marginTop: 14, fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.2)', lineHeight: 1.7, fontWeight: 300 }}>
              All fees are disclosed prior to trade confirmation. Mino does not charge hidden fees of any kind.
            </p>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Secondary Market Roadmap */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>The Future of Your Positions</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.18)', marginBottom: 20 }}>On the Roadmap</p>
                <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(24px,3vw,36px)', fontWeight: 700, color: '#e8e6e0', letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 24 }}>
                  Trade your positions.<br />Exit when you're ahead.
                </h3>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, marginBottom: 18, fontWeight: 300 }}>
                  Today, early exit comes with penalties — a deliberate design to protect market integrity at this stage.
                </p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, fontWeight: 300 }}>
                  As Mino grows, we're building a live secondary market where you can sell your position to another trader at any time before resolution. No penalties. No waiting.
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  ['Sell at a profit', 'Exit a winning position before resolution and pocket the gain'],
                  ['Peer-to-peer trades', 'Buy positions from other users at market-determined prices'],
                  ['Real-time pricing', 'Position values update live based on market movement'],
                ].map(([label, desc], i, a) => (
                  <div key={label} style={{ padding: '22px 0', borderBottom: i < a.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                    <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 600, color: '#e8e6e0', marginBottom: 6, letterSpacing: '-0.1px' }}>{label}</p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', lineHeight: 1.6, fontWeight: 300 }}>{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Resolution */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>Market Resolution</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72 }}>
              <div>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, color: '#e8e6e0', marginBottom: 16, letterSpacing: '-0.2px' }}>How outcomes are determined</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, marginBottom: 16, fontWeight: 300 }}>
                  When a market's resolution date is reached, the outcome is assessed against credible, publicly available information. Sources include established news organisations, official government publications, and recognised statistical bodies. No single source is used in isolation.
                </p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, fontWeight: 300 }}>
                  Where the factual record is contested or unavailable, the market may be extended or voided. In a void, all stakes are returned but not the fees, Fees are retained by the plartform.
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  ['Primary Sources', 'News orgs, official records'],
                  ['Disputes', 'Market extended or voided'],
                  ['Void Policy', 'Full stake return, except the fees'],
                ].map(([label, value], i, a) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0', borderBottom: i < a.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.2)', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
                    <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 13, color: '#e8e6e0', fontWeight: 500 }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* FAQ */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>Common Questions</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {FAQS.map((faq, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} className="faq-item" style={{ border: `1px solid ${isOpen ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.06)'}`, borderRadius: 12, overflow: 'hidden', background: isOpen ? 'rgba(255,255,255,0.03)' : 'transparent', transition: 'all 0.25s ease' }}>
                    <button onClick={() => setOpenFaq(isOpen ? null : i)} style={{ width: '100%', background: 'transparent', border: 'none', padding: '22px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 16, textAlign: 'left', fontFamily: 'inherit' }}>
                      <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 500, color: isOpen ? '#e8e6e0' : 'rgba(255,255,255,0.5)', transition: 'color 0.2s' }}>{faq.q}</span>
                      <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.2)', transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1)', transform: isOpen ? 'rotate(45deg)' : 'rotate(0)', flexShrink: 0, lineHeight: 1, fontWeight: 300 }}>+</span>
                    </button>
                    <div style={{ maxHeight: isOpen ? 400 : 0, overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.16,1,0.3,1)' }}>
                      <div style={{ padding: '0 28px 24px' }}>
                        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 18 }} />
                        <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, fontWeight: 300 }}>{faq.a}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </FadeUp>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* CTA */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div onClick={() => navigate('/markets')} style={{ background: '#e8e6e0', borderRadius: 16, padding: '40px 36px', cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#000', marginBottom: 10, letterSpacing: '-0.5px' }}>Browse the markets</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(0,0,0,0.45)', lineHeight: 1.6, marginBottom: 24, fontWeight: 400 }}>See what's live and place your first prediction.</p>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Go to markets
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
              </div>
              <div onClick={() => navigate('/responsible-gambling')} style={{ border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16, padding: '40px 36px', cursor: 'pointer', background: 'rgba(255,255,255,0.025)', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#e8e6e0', marginBottom: 10, letterSpacing: '-0.5px' }}>Responsible Trading</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6, marginBottom: 24, fontWeight: 300 }}>Tools and guidelines for staying in control.</p>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Read more
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
              </div>
            </div>
          </FadeUp>
        </div>

      </div>

      {/* Footer */}
      <div style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)', padding: '24px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', fontWeight: 300 }}>© 2026 Mino · DAFCOM ENTERPRISES</span>
        <span onClick={() => navigate('/markets')} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', cursor: 'pointer', fontWeight: 300, transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.48)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.16)'}>
          Back to markets
        </span>
      </div>
    </div>
  );
}